type DbSetup = (db: IDBDatabase) => void

interface MigrationSpec {
  oldName: string
  newName: string
  version: number
  setup: DbSetup
  stores: string[]
}

function getAllFromStore(db: IDBDatabase, storeName: string): Promise<unknown[]> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly')
    const req = tx.objectStore(storeName).getAll()
    req.onsuccess = () => resolve(req.result as unknown[])
    req.onerror = () => reject(req.error)
  })
}

function putAllToStore(db: IDBDatabase, storeName: string, records: unknown[]): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    for (const record of records) {
      store.put(record)
    }
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function migrateOneDb(spec: MigrationSpec): Promise<void> {
  // Open old DB without specifying a version — if onupgradeneeded fires with
  // oldVersion=0 the DB never existed and there is nothing to migrate.
  let didNotExist = false
  const oldDb = await new Promise<IDBDatabase | null>((resolve) => {
    const req = indexedDB.open(spec.oldName)
    req.onupgradeneeded = (e) => {
      if (e.oldVersion === 0) {
        didNotExist = true
        req.transaction?.abort()
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => resolve(null)
  })

  if (!oldDb || didNotExist) return

  const allData = new Map<string, unknown[]>()
  for (const store of spec.stores) {
    if (!oldDb.objectStoreNames.contains(store)) continue
    allData.set(store, await getAllFromStore(oldDb, store))
  }
  oldDb.close()

  const hasData = [...allData.values()].some((r) => r.length > 0)
  if (!hasData) {
    // Nothing worth copying — just delete the empty old DB
    indexedDB.deleteDatabase(spec.oldName)
    return
  }

  const newDb = await new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(spec.newName, spec.version)
    req.onupgradeneeded = () => spec.setup(req.result)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })

  for (const [store, records] of allData) {
    if (records.length === 0) continue
    await putAllToStore(newDb, store, records)
  }
  newDb.close()

  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(spec.oldName)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

const MIGRATIONS: MigrationSpec[] = [
  {
    oldName: 'imagevideo-keys',
    newName: 'byokapp-keys',
    version: 1,
    stores: ['keys'],
    setup: (db) => {
      db.createObjectStore('keys', { keyPath: 'provider' })
    },
  },
  {
    oldName: 'imagevideo-presets',
    newName: 'byokapp-presets',
    version: 1,
    stores: ['presets', 'thumbnails'],
    setup: (db) => {
      db.createObjectStore('presets', { keyPath: 'id' })
      db.createObjectStore('thumbnails', { keyPath: 'id' })
    },
  },
  {
    oldName: 'imagevideo-storage',
    newName: 'byokapp-storage',
    version: 1,
    stores: ['handles', 'credentials'],
    setup: (db) => {
      db.createObjectStore('handles', { keyPath: 'key' })
      db.createObjectStore('credentials', { keyPath: 'id' })
    },
  },
]

export async function migrateDbNames(): Promise<void> {
  await Promise.all(MIGRATIONS.map(migrateOneDb))
}
